import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppAvatarList from "@toothfairy/shared-ui/AppAvatarList";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppLabelCountIcon from "@toothfairy/shared-ui/AppLabelCountIcon";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View, TouchableOpacity, Text } from "react-native";
import { Popable } from "react-native-popable";
import { useReactiveVar } from "react-reactive-var";
import {
  forcedClosed,
  isElectronRunning,
  pinnedSideMenu,
  showSideMenu,
} from "../../../App";
import {
  forcedElectronChrome,
  forcedElectronChromePlatform,
  isElectronChrome,
} from "../../Utils/forceElectronChrome";
import Constants from "../../Constants";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory } from "../../Router";
import { routes } from "../../Router/routes";
import { isLargeScreen } from "../../Screens/MainViewContainer";
import ChatWidget from "../ChatWidget";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";
import ToothFairyChoice from "../ToothFairyComponents/ToothFairyChoice";
import AppAccountAvatar from "./Components/AppAccountAvatar";
import NotificationBell from "../Notifications/NotificationBell";
import WindowControls from "../WindowControls";
import CreditsIndicator from "../Credits/CreditsIndicator";
import { ENABLE_CONSUMPTION_BILLING } from "../../Constants/billing";
import envConfig from "../../../envConfig";
import {
  readFavouriteApp,
  writeFavouriteApp,
  subscribeFavouriteApp,
} from "../../Utils/favouriteApp";
import usersAPI from "../../API/Users/usersAPI";

dayjs.extend(relativeTime);

const b64encode = (s) => {
  try {
    const bytes = new TextEncoder().encode(String(s));
    let bin = "";
    bytes.forEach((b) => (bin += String.fromCharCode(b)));
    return btoa(bin).replace(/=+$/, "");
  } catch {
    return btoa(String(s)).replace(/=+$/, "");
  }
};

// tfcode embed backend origin. In production this is sourced from env/config.
// For localhost testing we fall back to the local dev server on :4096.
const WEB_TFCODE_ORIGIN =
  (typeof envConfig !== "undefined" && envConfig?.REACT_APP_TFCODE_ORIGIN) ||
  "http://localhost:4096";

// Builds the embed URL for the tfcode sidecar. Credentials are deliberately NOT
// embedded in the URL: Chromium silently discards subframe navigations whose URL
// contains basic-auth credentials, which left the Code tab on an infinite
// loading screen. Basic auth is answered by the Electron main process via the
// app.on("login") handler instead.
const tfcodeLocalUrl = (port) => `http://localhost:${port}`;

// Themes the native <select> popup for the right-side workspace switcher so it
// matches the surrounding app in both light and dark mode. RNW + Electron
// don't reliably inject emotion <Global> rules, so we manage a dedicated
// <style> tag directly and keep it in sync with theme changes.
const WorkspaceSelectStyle = ({ mode, currentTheme }) => {
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-tf-workspace-select-style", "true");
    const dark = mode === "dark";
    const optionBg = dark
      ? currentTheme?.theme?.backgroundContrastColor || "#2b2b2b"
      : "#ffffff";
    const optionColor = dark
      ? "#e6e6e6"
      : currentTheme?.theme?.darkGrey || "#333333";
    styleEl.textContent = `
      [data-workspace-select] select {
        color-scheme: ${dark ? "dark" : "light"};
      }
      [data-workspace-select] select:focus,
      [data-workspace-select] select:active,
      [data-workspace-select] select:focus-visible {
        outline: none;
        border-color: transparent;
        box-shadow: none;
      }
      [data-workspace-select] select option {
        background-color: ${optionBg};
        color: ${optionColor};
      }
    `;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, [mode, currentTheme?.theme?.backgroundContrastColor, currentTheme?.theme?.darkGrey]);
  return null;
};

const TopBar = ({
  isInWorkspace,
  handleSignOut,
  activeTab,
  setActiveTab,
  setTfcodeUrl,
  tfcodeUrl,
  setTfcodePanelUrl,
  setTfdesignUrl,
  tfdesignUrl,
  onToggleEmbeddedSidebar,
  onToggleEmbeddedDesignSidebar,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { supportUrl } = Constants;
  const showMenu = useReactiveVar(showSideMenu);
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showSupportWidget, setShowSupportWidget] = useState(false);
  const [showAskDesktopModal, setShowAskDesktopModal] = useState(false);
  const [showCloseWidgetConfirm, setShowCloseWidgetConfirm] = useState(false);
  const [lastWidgetOpened, setLastWidgetOpened] = useState(Date.now());
  // Remember the user's "don't ask again" choice for the install-desktop prompt
  // so they aren't shown it every time they click the Ask ToothFairyAI icon.
  const [dontAskInstallDesktop, setDontAskInstallDesktop] = useState(() => {
    try {
      return localStorage.getItem("tf_dont_ask_install_desktop") === "true";
    } catch (e) {
      return false;
    }
  });
  const handleDontAskInstallDesktopChange = useCallback((value) => {
    setDontAskInstallDesktop(value);
    try {
      localStorage.setItem(
        "tf_dont_ask_install_desktop",
        value ? "true" : "false",
      );
    } catch (e) {}
  }, []);
  const { user, updateUser: updateLocalUser } = AppUser.useUser();
  const [isCodeTabAvailable, setIsCodeTabAvailable] = useState(false);
  const [isDesignTabAvailable, setIsDesignTabAvailable] = useState(false);
  // Apps the user chose to install (unified desktop installer). Defaults to
  // all apps for web builds and older desktop builds without the bridge.
  const [installedApps, setInstalledApps] = useState(["app", "code", "design"]);
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI?.getSelectedApps) return;
    window.electronAPI
      .getSelectedApps()
      .then((config) => {
        if (Array.isArray(config?.apps) && config.apps.length) {
          setInstalledApps(config.apps);
        }
      })
      .catch(() => {});
  }, []);
  // Live-refresh when the user changes the selection in the in-session
  // wizard (open-apps-manager) — deselected apps must drop their tabs
  // without an app restart.
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI?.onSelectedAppsChanged) return;
    return window.electronAPI.onSelectedAppsChanged((config) => {
      if (Array.isArray(config?.apps) && config.apps.length) {
        setInstalledApps(config.apps);
      }
    });
  }, []);
  // Favourite app tab (right-click menu on the tab strip): persisted per
  // device; when set, MainViewScreen boots into that tab directly.
  const [favouriteApp, setFavouriteApp] = useState(readFavouriteApp());
  const [favouriteMenu, setFavouriteMenu] = useState(null);
  useEffect(() => subscribeFavouriteApp((value) => setFavouriteApp(value)), []);
  const prevWorkspaceIdRef = useRef(null);
  const tfcodeUrlRef = useRef(tfcodeUrl);
  useEffect(() => {
    tfcodeUrlRef.current = tfcodeUrl;
  }, [tfcodeUrl]);
  const tfdesignUrlRef = useRef(tfdesignUrl);
  useEffect(() => {
    tfdesignUrlRef.current = tfdesignUrl;
  }, [tfdesignUrl]);

  // Platform detection for Electron (real runtime or scoped browser override)
  const forcedChrome = useReactiveVar(forcedElectronChrome);
  const forcedPlatform = useReactiveVar(forcedElectronChromePlatform);
  const [realPlatform, setRealPlatform] = useState(null);
  useEffect(() => {
    if (isElectronRunning() && window.electronAPI && !forcedChrome) {
      window.electronAPI.getPlatform().then(setRealPlatform);
    }
  }, [forcedChrome]);
  const chrome = isElectronChrome();
  const platform = forcedChrome ? forcedPlatform : realPlatform;
  const isWindows = platform === "win32";
  const isLinux = platform === "linux";

  // Auto-update status surfaced by the Electron main process. Renders a small,
  // unobtrusive top-bar icon (no blocking dialog). state: null | "available" |
  // "downloaded".
  const [updateStatus, setUpdateStatus] = useState(null);
  // Debug/preview helper: in any console run
  //   __tfUpdateStatus("available" | "downloaded" | "blocked", "3.0.23")
  // to preview the icon, or __tfUpdateStatus(null) to clear it.
  useEffect(() => {
    window.__tfUpdateStatus = (state, version = "3.0.23") =>
      setUpdateStatus(state ? { state, version } : null);
    return () => {
      delete window.__tfUpdateStatus;
    };
  }, []);
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI) return;
    let cleanup;
    if (window.electronAPI.onUpdateStatus) {
      cleanup = window.electronAPI.onUpdateStatus((status) =>
        setUpdateStatus(status),
      );
    }
    if (window.electronAPI.getUpdateStatus) {
      window.electronAPI
        .getUpdateStatus()
        .then((status) => status && setUpdateStatus(status))
        .catch(() => {});
    }
    return () => cleanup?.();
  }, []);

  // Track workspace availability for Code tab
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI) return;

    const cleanupWorkspaceChanged = window.electronAPI.onWorkspaceChanged(
      async (workspace) => {
        const available = !!workspace?.workspaceToken;
        setIsCodeTabAvailable(available);
        setIsDesignTabAvailable(available);
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "Workspace changed event", {
            workspaceId: workspace?.id,
            available,
            currentTfcodeUrl: !!tfcodeUrlRef.current,
            currentTfdesignUrl: !!tfdesignUrlRef.current,
          });
        }

        // Clear tfcode URL when workspace changes so next Code click loads fresh
        if (workspace?.id !== prevWorkspaceIdRef.current) {
          prevWorkspaceIdRef.current = workspace?.id;
          setTfcodeUrl?.(null);
          setTfdesignUrl?.(null);
        }

        // Don't preload tfcode here — wait for agents to be loaded.
        // setProgrammerAgents in main.js will start tfcode once agents arrive.
        if (!available && activeTab === "code") {
          setActiveTab?.("app");
        }
        if (!available && activeTab === "design") {
          setActiveTab?.("app");
        }
      },
    );

    const cleanupTfcodeStarted = window.electronAPI.onTfcodeStarted
          ? window.electronAPI.onTfcodeStarted(({ port, password }) => {
          console.log("[TopBar] Tfcode started in background on port", port);
          if (window?.electronAPI?.writeLog) {
            window.electronAPI.writeLog("info", "Code started in background", {
              port,
              lan: !!password,
            });
          }
          setTfcodeUrl?.(tfcodeLocalUrl(port));
        })
      : undefined;

    const cleanupTfdesignStarted = window.electronAPI.onTfdesignStarted
      ? window.electronAPI.onTfdesignStarted(({ port }) => {
          console.log("[TopBar] TfDesign started in background on port", port);
          if (window?.electronAPI?.writeLog) {
            window.electronAPI.writeLog(
              "info",
              "Design started in background",
              {
                port,
              },
            );
          }
          const designTheme = mode === "dark" ? "dark" : "light";
          setTfdesignUrl?.(
            `http://localhost:${port}?embed=electron&theme=${designTheme}`,
          );
        })
      : undefined;

    window.electronAPI.getActiveWorkspace().then(async (workspace) => {
      const available = !!workspace?.workspaceToken;
      setIsCodeTabAvailable(available);
      setIsDesignTabAvailable(available);
      prevWorkspaceIdRef.current = workspace?.id;
      // NOTE: no boot bounce here — the main process only knows the active
      // workspace once the renderer restores it (set-workspace), so this
      // snapshot is always null on a fresh boot and an immediate check would
      // kick Code/Design-favourite users off their tab. The grace-window
      // effect below handles the genuinely-no-workspace case instead.
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog("info", "TopBar mount — workspace check", {
          workspaceId: workspace?.id,
          available,
          currentTfcodeUrl: !!tfcodeUrlRef.current,
          currentTfdesignUrl: !!tfdesignUrlRef.current,
        });
      }
      // Don't preload tfcode here — agents may not be loaded yet.
      // setProgrammerAgents in main.js starts tfcode once agents arrive.
    });

    return () => {
      cleanupWorkspaceChanged?.();
      cleanupTfcodeStarted?.();
      cleanupTfdesignStarted?.();
    };
  }, [activeTab, tfcodeUrl, tfdesignUrl]);

  // Boot fallback: if the restored tab is Code/Design and no workspace ever
  // becomes available (e.g. after a region switch wiped the account), fall
  // back to the App tab so the workspace picker is reachable. The workspace
  // state restores asynchronously after boot, so this waits a grace window —
  // an immediate check always reports "no workspace" on a fresh boot and
  // would bounce users whose favourite tab is Code/Design. When a workspace
  // does arrive (isCodeTabAvailable flips true) the timer is cleared.
  useEffect(() => {
    if (activeTab !== "code" && activeTab !== "design") return undefined;
    if (isCodeTabAvailable) return undefined;
    const fallbackTimer = setTimeout(() => {
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog(
          "info",
          "No workspace became available — falling back to App tab",
          { activeTab },
        );
      }
      setActiveTab?.("app");
    }, 15000);
    return () => clearTimeout(fallbackTimer);
  }, [activeTab, isCodeTabAvailable]);

  const handleTabSwitch = useCallback(
    async (tab) => {
      if (!window.electronAPI || tab === activeTab) return;

      // Remember the last app so workspace changes / reloads land back on it.
      try {
        localStorage.setItem("tf_last_app", tab);
      } catch (_) {}

      if (tab === "code" && isCodeTabAvailable) {
        const currentUrl = tfcodeUrlRef.current;
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "Code tab clicked", {
            tfcodeUrlPresent: !!currentUrl,
            isCodeTabAvailable,
          });
        }
        setActiveTab?.("code");
        if (currentUrl) return;
        try {
          const { port } = await window.electronAPI.startTfcode();
          setTfcodeUrl?.(`http://localhost:${port}`);
        } catch (error) {
          console.error("[TopBar] Failed to start tfcode:", error);
          setActiveTab?.("app");
          if (window?.electronAPI?.writeLog) {
            window.electronAPI.writeLog("error", "Code tab failed to start", {
              error: error?.message,
            });
          }
          AppToasty.show(t("code.startFailed"), {
            placement: "top",
            type: "danger",
          });
        }
      } else if (tab === "design" && isDesignTabAvailable) {
        const currentUrl = tfdesignUrlRef.current;
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "Design tab clicked", {
            tfdesignUrlPresent: !!currentUrl,
            isDesignTabAvailable,
          });
        }
        setActiveTab?.("design");
        if (currentUrl) return;
        try {
          const { port } = await window.electronAPI.startTfdesign();
          const designTheme = mode === "dark" ? "dark" : "light";
          setTfdesignUrl?.(
            `http://localhost:${port}?embed=electron&theme=${designTheme}`,
          );
        } catch (error) {
          console.error("[TopBar] Failed to start tf-design:", error);
          setActiveTab?.("app");
          if (window?.electronAPI?.writeLog) {
            window.electronAPI.writeLog("error", "Design tab failed to start", {
              error: error?.message,
            });
          }
          AppToasty.show(t("design.startFailed"), {
            placement: "top",
            type: "danger",
          });
        }
      } else if (tab === "app") {
        setActiveTab?.("app");
      }
    },
    [
      activeTab,
      isCodeTabAvailable,
      isDesignTabAvailable,
      setActiveTab,
      setTfcodeUrl,
      setTfdesignUrl,
    ],
  );

  const AppToasty = AppToast.useToast();

  // Derive the first bound folder for the active workspace (used as the embed dir).
  const firstBoundFolder = useCallback(() => {
    const activeId = state?.activeWorkspaceID;
    for (const c of state?.chats || []) {
      if (c?.workspaceID !== activeId) continue;
      const meta =
        typeof c?.metadata === "string" ? JSON.parse(c.metadata) : c?.metadata;
      if (meta?.folderBinding?.path) return meta.folderBinding.path;
    }
    return undefined;
  }, [state?.activeWorkspaceID, state?.chats]);

  // Resolve the embedded tfcode URL. On Electron it starts the tfcode sidecar;
  // on web it falls back to the local dev backend (:4096). The result is handed
  // to the side panel (which sits ON TOP of the app — no tab switching).
  const resolveTfcodeUrl = useCallback(async (folderPath) => {
    const parent = typeof window !== "undefined" ? window.location.origin : "";
    if (window?.electronAPI?.startTfcode) {
      try {
        const { port } = await window.electronAPI.startTfcode();
        const base = new URL(`${tfcodeLocalUrl(port)}/embed.html`);
        if (folderPath) base.searchParams.set("dir", b64encode(folderPath));
        if (parent) base.searchParams.set("parent", parent);
        return base.toString();
      } catch (error) {
        console.error("[TopBar] Ask ToothFairyAI start failed:", error);
        window?.electronAPI?.writeLog?.("error", "tfcode start failed", {
          error: error?.message,
        });
      }
    }
    const base = new URL(`${WEB_TFCODE_ORIGIN}/embed.html`);
    if (folderPath) base.searchParams.set("dir", b64encode(folderPath));
    base.searchParams.set("server", WEB_TFCODE_ORIGIN);
    if (parent) base.searchParams.set("parent", parent);
    return base.toString();
  }, []);

  // Open the legacy web widget (generic ToothFairyAI agent at agent.toothfairyai.com).
  const openWebWidget = useCallback(() => {
    setShowSupportWidget((open) => !open);
    setLastWidgetOpened(Date.now());
  }, []);

  // The "Ask ToothFairyAI" icon. On the desktop app (Electron) it opens the TF
  // Engineer side panel. On the web it opens the legacy generic agent widget —
  // unless the user hasn't installed the desktop app yet, in which case we first
  // offer to install it and only fall back to the web widget as an alternative.
  const handleAskToothFairy = useCallback(() => {
    if (isElectronRunning()) {
      // The side panel is tfcode-powered; without the Code app installed it
      // can't start, so fall back to the web widget.
      if (!installedApps.includes("code")) {
        openWebWidget();
        return;
      }
      const folderPath = firstBoundFolder();
      resolveTfcodeUrl(folderPath).then((u) => setTfcodePanelUrl?.(u));
      return;
    }
    // If the widget is already open (icon underlined), warn before closing —
    // closing it discards the current conversation. This takes priority over
    // the desktop-app prompt so an already-open widget can always be closed.
    if (showSupportWidget) {
      setShowCloseWidgetConfirm(true);
      return;
    }
    if (user?.desktopAppInstalled || dontAskInstallDesktop) {
      openWebWidget();
      return;
    }
    setShowAskDesktopModal(true);
  }, [
    isElectronRunning,
    firstBoundFolder,
    resolveTfcodeUrl,
    showSupportWidget,
    user?.desktopAppInstalled,
    dontAskInstallDesktop,
    openWebWidget,
    installedApps,
  ]);

  const { currentTheme, mode } = AppTheme.useTheme();
  const styles = generateStyles({
    currentTheme,
    electron: chrome,
    platform,
  });
  const { updatePath, params } = usePath();
  const [showModal, setShowModal] = useState(false);
  const {
    getActiveWorkspace,
    returnCleanedWorkspaces,
    getWorkspaceUsers,
    isUserAdmin,
  } = useWorkspaces();

  const supportUrls = (section) => {
    switch (section) {
      case "DASHBOARD_MODEL":
        return `${supportUrl}/i/32`;
      case "NLP_PARSER":
        return `${supportUrl}/i/6`;
      case "PROFILE_ANALYSIS":
        return `${supportUrl}/i/34`;
      default:
        return supportUrl;
    }
  };
  const _workspaceDropDown = useMemo(() => {
    const cleanedWorkspaces = returnCleanedWorkspaces(state?.workspaces);
    const hasMultipleWorkspaces = false;

    return (
      isLargeScreen() &&
      isInWorkspace &&
      (hasMultipleWorkspaces ? (
        <AppDropDown
          dropDownStyle={{
            width: isLargeScreen() ? 300 : 250,
            marginLeft: 0,
            borderColor: currentTheme?.theme?.border_color,
            fontWeight: "800",
          }}
          isElevated={false}
          dropdownIconColor="transparent"
          items={cleanedWorkspaces}
          propertyLabel="title"
          propertyValue="id"
          selectedValue={state?.activeWorkspaceID}
          onValueChange={(itemValue, itemIndex) => {
            dispatch("SET_ACTIVE_WORKSPACE_ID", itemValue);
            updatePath({
              workspaceId: itemValue,
              sectionId: params?.sectionId,
            });
            window.location.reload();
          }}
        />
      ) : (
        <AppText
          style={{
            fontSize: 16,
            fontWeight: "800",
            marginLeft: 0,
            marginTop: 1,
            maxWidth: isLargeScreen() ? 300 : 250,
          }}
          color={currentTheme?.theme?.darkGrey}
          numberOfLines={1}
        >
          {getActiveWorkspace()?.title || cleanedWorkspaces?.[0]?.title || ""}
        </AppText>
      ))
    );
  }, [
    isLargeScreen(),
    isInWorkspace,
    currentTheme,
    JSON.stringify(state?.workspaces),
    updatePath,
    returnCleanedWorkspaces,
    getActiveWorkspace,
    params,
  ]);
  const _trialEndingCaption = useMemo(() => {
    return (
      isInWorkspace &&
      user?.stripeCustomerData?.subscriptionIdOnTrial ===
        getActiveWorkspace()?.subscriptionId && <></>
    );
  }, [
    isInWorkspace,
    user?.id,
    JSON.stringify(currentTheme),
    user?.stripeCustomerData?.subscriptionIdOnTrial,
    getActiveWorkspace()?.subscriptionId,
  ]);

  const _activeBalanceCaption = useMemo(() => {
    return (
      user?.stripeCustomerData?.balance < 0 && (
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 300,
            },
          ]}
          content={t("topbar.creditBalance")}
          action="hover"
          position="bottom"
        >
          <AppText
            wrapperStyle={{ marginRight: 10, marginTop: 5 }}
            color={currentTheme?.theme?.green}
            style={{ fontSize: currentTheme?.theme?.extra_small_font_size }}
            fontWeight="bold"
          >{`$${user?.stripeCustomerData?.balance / -100}`}</AppText>
        </Popable>
      )
    );
  }, [isInWorkspace, user?.id, user?.stripeCustomerData?.balance]);

  const _billingStatusIndicator = useMemo(() => {
    const hasPaymentMethod =
      user?.stripeCustomerData?.invoice_settings?.default_payment_method !==
      null;
    const hasBillingAddress =
      user?.stripeCustomerData?.address !== null &&
      user?.stripeCustomerData?.address?.line1 &&
      user?.stripeCustomerData?.address?.city &&
      user?.stripeCustomerData?.address?.country;

    if (hasPaymentMethod && hasBillingAddress) return null;

    const missingItems = [];
    if (!hasPaymentMethod) missingItems.push(t("topbar.paymentMethod"));
    if (!hasBillingAddress) missingItems.push(t("topbar.billingDetails"));

    return missingItems?.length > 0 ? (
      <Popable
        numberOfLines={5}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 600,
          },
        ]}
        content={t("topbar.missingBillingInfo", {
          items: missingItems.join(" & "),
        })}
        action="hover"
        position="bottom"
      >
        <AppIcon
          isClickable
          size={16}
          icon={AppAssets.icons.billingDetails}
          color={currentTheme?.theme?.red}
          wrapperStyle={{
            marginRight: 5,
            marginTop: 4,
          }}
          onPress={() => {
            history.push("/profile/billing");
          }}
        />
      </Popable>
    ) : (
      <></>
    );
  }, [user?.stripeCustomerData, currentTheme, history, t]);
  const _menuIcon = useMemo(() => {
    return (
      <AppIcon
        size={16}
        isClickable
        icon={AppAssets.icons.menu}
        wrapperStyle={{
          marginLeft: 10,
          marginRight: 10,
        }}
        onPress={() => {
          // On the Code tab the app sidebar is hidden; the hamburger drives the
          // embedded tfcode sidebar instead, so the user's collapse muscle memory
          // carries across both apps.
          if (activeTab === "code") {
            onToggleEmbeddedSidebar?.();
            return;
          }
          // On the Design tab the hamburger toggles the embedded tf-design
          // sidebar the same way.
          if (activeTab === "design") {
            onToggleEmbeddedDesignSidebar?.();
            return;
          }
          showSideMenu(!showMenu);
          forcedClosed(!forcedClosed);
          pinnedSideMenu(false);
        }}
      />
    );
  }, [
    showMenu,
    chrome,
    platform,
    activeTab,
    onToggleEmbeddedSidebar,
    onToggleEmbeddedDesignSidebar,
  ]);

  const isBusinessOrEnterprise =
    getActiveWorkspace()?.subscriptionType === "business" ||
    getActiveWorkspace()?.subscriptionType === "enterprise";

  const _usersInWorkspace = useMemo(() => {
    return (
      isInWorkspace &&
      isBusinessOrEnterprise && (
        <View
          style={{ flexDirection: "row", alignItems: "center", marginTop: 4 }}
        >
          <Popable
            numberOfLines={2}
            backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
            style={[
              {
                top: 30,
                zIndex: 20,
                maxWidth: 300,
              },
            ]}
            content={t("topbar.workspaceUsers")}
            action="hover"
            position="bottom"
          >
            <AppLabelCountIcon
              shouldShowOne={false}
              // count={getWorkspaceUsers()?.length}
              labelCountIconViewStyle={{
                marginTop: -7,
                marginRight: 0,
              }}
              marginRight={4}
              isDescriptionVisible={false}
              label={"wk_users"}
              icon={AppAssets.icons.usersSettings}
              color={currentTheme?.theme?.primary}
              wrapperCountLabelStyle={{
                right: 10,
                top: -2,
              }}
              countColor={currentTheme?.theme?.primary}
              activeColor={currentTheme?.theme?.primary}
              countLabelStyle={{
                fontWeight: "bold",
                fontSize: currentTheme?.theme?.extra_small_font_size,
              }}
              size={18}
              onPress={() => {
                if (isUserAdmin())
                  history.push(
                    `/workspaces/${getActiveWorkspace()?.id}/admin/users`,
                  );
              }}
            />
          </Popable>
        </View>
      )
    );
  }, [
    isInWorkspace,
    isBusinessOrEnterprise,
    currentTheme,
    getWorkspaceUsers(),
    getActiveWorkspace()?.userID,
    user?.id,
  ]);

  const _regionIcon = useMemo(() => {
    const regionEnv = envConfig.REACT_APP_ENV;
    const regionIcon = AppAssets.icons[regionEnv] || AppAssets.icons.dev;

    const regionNameMap = {
      produs: "USA",
      prodeu: "Europe",
      prod: "Australia",
      prodau: "Australia",
      dev: "Australia",
    };

    const regionName = regionNameMap[regionEnv] || regionEnv;

    return (
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("topbar.region", { name: regionName })}
        action="hover"
        position="bottom"
      >
        <AppIcon
          size={16}
          icon={regionIcon}
          color={currentTheme?.theme?.primary}
          wrapperStyle={{ marginRight: 10, marginTop: 4 }}
        />
      </Popable>
    );
  }, [currentTheme]);

  const _desktopAppInstallButton = useMemo(() => {
    if (chrome || user?.desktopAppInstalled) return null;

    const handleInstallClick = async () => {
      try {
        await usersAPI.updateUser({
          id: user?.id,
          desktopAppInstalled: true,
        });
        updateLocalUser({
          ...user,
          desktopAppInstalled: true,
        });
        window.open("https://toothfairyai.com/download", "_blank");
      } catch (error) {
        console.error("Failed to update desktopAppInstalled:", error);
      }
    };

    return (
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("topbar.installDesktopApp")}
        action="hover"
        position="bottom"
      >
        <AppText
          isClickable
          onPress={handleInstallClick}
          icon={AppAssets.icons.download}
          color={currentTheme?.theme?.primary}
          style={{
            fontSize: currentTheme?.theme?.extra_small_font_size,
            fontWeight: "bold",
          }}
          wrapperStyle={{
            marginTop: 4,
            marginRight: 5,
          }}
        >
          {t("topbar.installDesktopClient")}
        </AppText>
      </Popable>
    );
  }, [chrome, user?.desktopAppInstalled, user?.id, updateLocalUser]);
  const _activeUsersLists = useMemo(() => {
    return (
      isInWorkspace && (
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 300,
            },
          ]}
          content={t("topbar.workspaceUsers")}
          action="hover"
          position="bottom"
        >
          <AppAvatarList
            avatarSize={20}
            avatarRatio={0.5}
            labelProperty="username"
            namesList={state?.activeWorkspaceUsers}
            wrapperStyle={{
              top: 2,
              marginRight: 10,
            }}
          />
        </Popable>
      )
    );
  }, [
    isInWorkspace,
    JSON.stringify(
      state?.activeWorkspaceUsers,
      currentTheme?.theme?.tooltipBackgroundColor,
    ),
  ]);
  const _electronTabs = useMemo(() => {
    // Only render the tab strip when more than one app is installed: a single
    // app boots straight into it (MainViewScreen restores it as activeTab).
    if (!chrome || !isInWorkspace || installedApps.length <= 1) return null;

    const trackBg =
      mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
    const activeTabBg =
      mode === "dark"
        ? currentTheme?.theme?.backgroundContrastColor || "#3b3b3b"
        : currentTheme?.theme?.white || "#fff";
    const activeTextColor = currentTheme?.theme?.primary;

    const tabStyle = (isActive, isDisabled) => ({
      paddingHorizontal: 16,
      paddingVertical: 5,
      borderRadius: 6,
      backgroundColor: isActive ? activeTabBg : "transparent",
      opacity: isDisabled ? 0.4 : 1,
    });

    const textStyle = (isActive) => ({
      color: isActive ? activeTextColor : currentTheme?.theme?.darkGrey,
      fontSize: 13,
      fontWeight: isActive ? "600" : "500",
    });

    const Tab = ({ id, label, disabled }) => (
      <div
        style={{ display: "flex" }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Clamp so the menu never overflows the viewport edge.
          setFavouriteMenu({
            x: Math.min(e.clientX, window.innerWidth - 170),
            y: Math.min(e.clientY + 4, window.innerHeight - 60),
            tab: id,
          });
        }}
      >
        <TouchableOpacity
          style={tabStyle(activeTab === id, disabled)}
          onPress={() => handleTabSwitch(id)}
          disabled={disabled}
        >
          <Text preventTranslation style={textStyle(activeTab === id)}>
            {label}
          </Text>
        </TouchableOpacity>
      </div>
    );

    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginLeft: 12,
          backgroundColor: trackBg,
          borderRadius: 8,
          padding: 3,
        }}
      >
        {installedApps.includes("app") && (
          <Tab id="app" label={t("topbar.app")} disabled={false} />
        )}
        {installedApps.includes("code") && (
          <Tab id="code" label="Code" disabled={!isCodeTabAvailable} />
        )}
        {installedApps.includes("design") && (
          <Tab id="design" label="Design" disabled={!isDesignTabAvailable} />
        )}
      </View>
    );
  }, [
    chrome,
    isInWorkspace,
    activeTab,
    installedApps,
    isCodeTabAvailable,
    isDesignTabAvailable,
    currentTheme,
    mode,
    handleTabSwitch,
    favouriteApp,
    setFavouriteMenu,
    t,
  ]);
  const _favouriteContextMenu = useMemo(() => {
    if (!favouriteMenu) return null;
    const menuBg =
      currentTheme?.theme?.backgroundContrastColor ||
      (mode === "dark" ? "#2f2f2f" : "#ffffff");
    const border =
      currentTheme?.theme?.borderColor ||
      (mode === "dark" ? "#444444" : "#d7e0e4");
    const textColor = currentTheme?.theme?.font_color || "#1a2b33";
    const hoverBg = mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)";
    const isFavourite = favouriteApp === favouriteMenu.tab;
    return (
      <View
        dataSet={{ "favourite-menu-backdrop": "" }}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
        }}
        onClick={() => setFavouriteMenu(null)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          dataSet={{ "favourite-menu": "" }}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            left: favouriteMenu.x,
            top: favouriteMenu.y,
            zIndex: 100000,
            minWidth: 160,
            background: menuBg,
            border: `1px solid ${border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
            padding: 4,
          }}
        >
          <div
            onClick={() => {
              writeFavouriteApp(isFavourite ? null : favouriteMenu.tab);
              setFavouriteMenu(null);
            }}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              color: textColor,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = hoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {isFavourite ? "Clear Favourite" : "Set as Favourite"}
          </div>
        </div>
      </View>
    );
  }, [favouriteMenu, favouriteApp, currentTheme, mode, setFavouriteMenu]);

  // Electron right-side workspace selector. A compact dropdown of the user's
  // workspaces so they can switch straight from the top bar while staying in
  // the active app tab; falls back to the plain title when they have zero or
  // one workspace (a dropdown with a single item is pointless).
  const _electronWorkspaceDropDown = useMemo(() => {
    const cleanedWorkspaces = returnCleanedWorkspaces(state?.workspaces) || [];
    const activeTitle =
      getActiveWorkspace()?.title || cleanedWorkspaces?.[0]?.title || "";
    if (cleanedWorkspaces.length <= 1) {
      return (
        <AppText
          style={{
            fontSize: 12,
            lineHeight: 15,
            fontWeight: "600",
            marginTop: 4,
            marginRight: 5,
            maxWidth: 160,
          }}
          color={currentTheme?.theme?.darkGrey}
          numberOfLines={1}
        >
          {activeTitle}
        </AppText>
      );
    }
    // Sentinel first option: jumps to the workspace list (where the user can
    // star a favourite, create or switch workspaces). Its id can never clash
    // with a real workspace (UUIDs).
    const WORKSPACES_OPTION_ID = "__workspaces_list__";
    const dropdownItems = [
      { id: WORKSPACES_OPTION_ID, title: t("workspaces.title") },
      ...cleanedWorkspaces,
    ];
    return (
      <View dataSet={{ "workspace-select": "true" }}>
        <AppDropDown
          dropDownStyle={{
            width: 160,
            fontWeight: "600",
            // Transparent trigger with no border so it blends into the top
            // bar in both light and dark mode; the popup options are themed
            // via the emotion Global styles below.
            backgroundColor: "transparent",
            borderWidth: 0,
            borderColor: "transparent",
          }}
          isElevated={false}
          dropdownIconColor={currentTheme?.theme?.darkGrey}
          items={dropdownItems}
          propertyLabel="title"
          propertyValue="id"
          selectedValue={state?.activeWorkspaceID}
          onValueChange={(itemValue) => {
            if (itemValue === WORKSPACES_OPTION_ID) {
              history.push(routes.workspaces);
              return;
            }
            // Switch is pure SPA navigation (same flow as selecting a
            // workspace from the workspace list): window.location.reload()
            // breaks under file:// in Electron, so never reload here. The
            // home screen mounts fresh and fully re-fetches for the new
            // workspace, and the active app tab (Code/Design) is preserved.
            const workspace = state?.workspaces?.find((w) => w?.id === itemValue);
            if (workspace?.isMFAEnabled && user?.isMFAEnabled !== true) {
              history.push("/mfa_required");
              return;
            }
            dispatch("SET_ACTIVE_WORKSPACE_ID", itemValue);
            history.push(`/workspaces/${itemValue}/home`);
          }}
        />
      </View>
    );
  }, [
    currentTheme,
    JSON.stringify(state?.workspaces),
    state?.activeWorkspaceID,
    user,
    history,
    dispatch,
    returnCleanedWorkspaces,
    getActiveWorkspace,
    t,
  ]);

  const _topBarView = useMemo(() => {
    return (
      <View style={styles.container}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {chrome && platform !== "darwin" && (
            <AppIcon
              style={{
                width: 28,
                marginLeft: 10,
                marginRight: 8,
              }}
              isClickable
              onPress={() => {
                history.push(routes.workspaces);
              }}
              size={20}
              imageSource={require("../../assets/icon.png")}
            />
          )}
          <View style={{ width: 40 }}>{_menuIcon}</View>
          {!chrome && _workspaceDropDown}
        </View>
        {_electronTabs}
        {chrome && <div className="electron_titlebar" />}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            // marginRight: isWindows && 70,
          }}
        >
          {isInWorkspace && chrome ? _electronWorkspaceDropDown : null}
          <View
            style={{ width: isUserAdmin() ? "auto" : 0, overflow: "hidden" }}
          >
            {_activeUsersLists}
          </View>
          <View
            style={{ width: isUserAdmin() ? "auto" : 0, overflow: "hidden" }}
          >
            {_trialEndingCaption}
          </View>
          {!ENABLE_CONSUMPTION_BILLING && (
            <View
              style={{ width: isUserAdmin() ? "auto" : 0, overflow: "hidden" }}
            >
              {_activeBalanceCaption}
            </View>
          )}
          {ENABLE_CONSUMPTION_BILLING && isInWorkspace && (
            <CreditsIndicator
              onOpenSettings={() => history.push("/profile/billing")}
            />
          )}
          <View style={{ width: "auto", overflow: "hidden" }}>
            {_billingStatusIndicator}
          </View>
          <View
            style={{
              width: state?.isWorkspaceCollabReady === false ? "auto" : 0,
              overflow: "hidden",
            }}
          >
            {state?.isWorkspaceCollabReady === false && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={t("topbar.noNetwork")}
                action="hover"
                position="bottom"
              >
                <AppIcon
                  size={16}
                  icon={AppAssets.icons.networkError}
                  color={currentTheme?.theme?.orange}
                  wrapperStyle={{ marginTop: 4, marginRight: 5 }}
                />
              </Popable>
            )}
          </View>

          <View
            style={{
              flexDirection: "row",
              gap: 5,
              alignItems: "center",
            }}
          >
            {chrome && updateStatus?.state && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[{ top: 30, zIndex: 20, maxWidth: 300 }]}
                content={
                  updateStatus.state === "downloaded"
                    ? t("topbar.updateDownloaded", {
                        defaultValue: "Update ready",
                      })
                    : t("topbar.updateAvailable", {
                        defaultValue: "Update downloading",
                      })
                }
                action="hover"
                position="bottom"
              >
                <AppIcon
                  size={16}
                  // Clickable when ready so the user always has a working
                  // "restart to update" path even if install-on-quit misbehaves.
                  isClickable={updateStatus.state === "downloaded"}
                  onPress={
                    updateStatus.state === "downloaded"
                      ? () => window.electronAPI?.installUpdate?.()
                      : undefined
                  }
                  icon={
                    updateStatus.state === "downloaded"
                      ? AppAssets.icons.reload
                      : AppAssets.icons.download
                  }
                  color={
                    updateStatus.state === "downloaded"
                      ? currentTheme?.theme?.primary
                      : currentTheme?.theme?.orange
                  }
                  wrapperStyle={{ marginTop: 4, marginLeft: 4 }}
                />
              </Popable>
            )}
            {_desktopAppInstallButton}
            <Popable
              numberOfLines={2}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  top: 30,
                  zIndex: 20,
                  maxWidth: 300,
                },
              ]}
              content={
                isElectronRunning()
                  ? t("topbar.askTfEngineer")
                  : t("topbar.askToothFairy")
              }
              action="hover"
              position="bottom"
            >
              <AppIcon
                isClickable
                onPress={handleAskToothFairy}
                // imageSource={require("../../assets/ai.png")}
                icon={AppAssets.icons.question_icon}
                width={"100%"}
                size={15}
                wrapperStyle={{
                  height: 24,
                  width: 24,
                  marginTop: 5,
                  marginRight: isBusinessOrEnterprise && isInWorkspace ? 0 : 5,
                  borderBottomColor: showSupportWidget
                    ? currentTheme?.theme?.primary
                    : currentTheme?.theme?.transparent,
                  borderBottomWidth: 1,
                }}
                style={{
                  height: 24,
                  width: 24,
                }}
              />
            </Popable>
          </View>

          <View
            style={{
              marginBottom: 2,
              marginRight: isInWorkspace && -5,
            }}
          >
            <NotificationBell />
          </View>

          <View
            style={{
              width: isUserAdmin() && isBusinessOrEnterprise ? "auto" : 0,
              overflow: "hidden",
            }}
          >
            {_usersInWorkspace}
          </View>
          <View>{_regionIcon}</View>
          <View>
            <Popable
              numberOfLines={2}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  top: 30,
                  zIndex: 20,
                  maxWidth: 300,
                },
              ]}
              content={t("topbar.account")}
              action="hover"
              position="left"
            >
              <AppAccountAvatar handleSignOut={handleSignOut} user={user} />
            </Popable>
          </View>
        </View>
        {(isWindows || isLinux) && <WindowControls />}
      </View>
    );
  }, [
    _menuIcon,
    _electronTabs,
    _electronWorkspaceDropDown,
    showSupportWidget,
    _workspaceDropDown,
    _trialEndingCaption,
    _billingStatusIndicator,
    getWorkspaceUsers(),
    isUserAdmin(),
    isBusinessOrEnterprise,
    chrome,
    mode,
    state?.isWorkspaceCollabReady,
    isWindows,
    isLinux,
    updateStatus,
    currentTheme,
    t,
    handleAskToothFairy,
  ]);
  return (
    <>
      {chrome && (
        <WorkspaceSelectStyle mode={mode} currentTheme={currentTheme} />
      )}
      {_topBarView}
      {_favouriteContextMenu}
      {showSupportWidget ? <ChatWidget isOpen={showSupportWidget} /> : <></>}
      <ToothFairyModal
        isModalOpen={showAskDesktopModal}
        setIsModalOpen={setShowAskDesktopModal}
        closeModal={() => setShowAskDesktopModal(false)}
        modalStyle={{
          height: 292,
          width: "90%",
          maxWidth: isLargeScreen() ? 460 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
      >
        <View
          style={[
            styles.dialogContainer,
            { backgroundColor: currentTheme?.theme?.backgroundColor },
          ]}
        >
          <Text
            style={[
              styles.dialogTitle,
              { color: currentTheme?.theme?.font_color },
            ]}
          >
            {t("topbar.askDesktopTitle")}
          </Text>
          <Text
            style={[
              styles.dialogMessage,
              { color: currentTheme?.theme?.font_color },
            ]}
          >
            {t("topbar.askDesktopMessage")}
          </Text>
          <View style={styles.dialogActions}>
            <ToothFairyButton
              title={t("topbar.askDesktopDownload")}
              primary
              onPress={() => {
                setShowAskDesktopModal(false);
                window.open("https://toothfairyai.com/en/download", "_blank");
              }}
              buttonStyle={styles.dialogButton}
            />
            <ToothFairyButton
              title={t("topbar.askDesktopUseWeb")}
              isSecondary
              fontColor={currentTheme?.theme?.primary}
              onPress={() => {
                setShowAskDesktopModal(false);
                openWebWidget();
              }}
              buttonStyle={styles.dialogButton}
            />
          </View>
          <TouchableOpacity
            activeOpacity={0.6}
            onPress={() =>
              handleDontAskInstallDesktopChange(!dontAskInstallDesktop)
            }
            style={{
              flexDirection: "row",
              alignItems: "center",
              alignSelf: "flex-start",
              marginTop: 16,
            }}
          >
            <View
              style={{
                width: 16,
                height: 16,
                borderRadius: 3,
                borderWidth: 1.5,
                borderColor: currentTheme?.theme?.mediumGrey,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: dontAskInstallDesktop
                  ? currentTheme?.theme?.mediumGrey
                  : "transparent",
              }}
            >
              {dontAskInstallDesktop ? (
                <AppIcon
                  icon={AppAssets.icons.tick}
                  size={10}
                  color={currentTheme?.theme?.backgroundColor}
                />
              ) : null}
            </View>
            <Text
              style={{
                marginLeft: 8,
                fontSize: 12,
                color: currentTheme?.theme?.mediumGrey,
              }}
            >
              {t("topbar.askDesktopDontAskAgain")}
            </Text>
          </TouchableOpacity>
        </View>
      </ToothFairyModal>
      <ToothFairyChoice
        showChoice={showCloseWidgetConfirm}
        setShowChoice={setShowCloseWidgetConfirm}
        caption={t("topbar.closeWidgetTitle")}
        subCaption={t("topbar.closeWidgetMessage")}
        confirmCaption={t("topbar.closeWidgetConfirm")}
        cancelCaption={t("common.cancel")}
        onCancelButtonPress={() => setShowCloseWidgetConfirm(false)}
        onConfirmButtonPress={() => {
          setShowCloseWidgetConfirm(false);
          openWebWidget();
        }}
      />
    </>
  );
};

const generateStyles = ({ currentTheme, electron, platform }) =>
  StyleSheet.create({
    container: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      paddingVertical: 5,
      paddingLeft: 5,
      paddingRight: 15,
      height: 35,
      zIndex: 10,
      backgroundColor: currentTheme?.theme?.backgroundColorAppBorders,
      borderBottomColor: currentTheme?.theme?.app_outline_color,
      borderBottomWidth: 1,
      opacity: 0.9,
      // On Electron, use fixed positioning to span full width
      // This prevents the TopBar from moving when sidebar opens/closes
      ...(electron
        ? {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            // Fully opaque: the fixed bar overlaps the sidebar header logo,
            // and 0.9 opacity lets it bleed through behind the app icon
            opacity: 1,
            ...(platform === "darwin" ? { paddingLeft: 80 } : {}),
          }
        : {}),
    },
    modal: {
      height: "95%",
      marginHorizontal: Platform.OS === "web" ? "auto" : "5%",
      marginVertical: "auto",
      width: Platform.OS === "web" ? "60%" : "90%",
      maxWidth: 600,
    },
    listContentContainer: {
      padding: 0,
    },
    listHeader: {
      backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
    },
    dialogContainer: {
      borderRadius: 12,
      padding: 24,
      paddingTop: 20,
      gap: 14,
    },
    dialogTitle: {
      fontSize: 17,
      fontWeight: "bold",
    },
    dialogMessage: {
      fontSize: 13,
      lineHeight: 19,
      opacity: 0.85,
    },
    dialogActions: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: 10,
      marginTop: 40,
    },
    dialogButton: {
      margin: 0,
      minWidth: 150,
    },
  });

export default TopBar;
